import React, { useState, useRef, useEffect } from 'react';
import './Contact.css';
import { FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Corrigir ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Contact = () => {
  const formRef = useRef();
  const mapRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);


  const EMAILJS_CONFIG = {
    SERVICE_ID: 'Infosfera Service',      
    TEMPLATE_ID: 'template_3eag5pm',  
    PUBLIC_KEY: '42O4uwaxHTTMItZTj'      
  };

  // Inicializar mapa de fundo
  useEffect(() => {
    if (mapRef.current && !mapRef.current.hasMap) {
      const map = L.map(mapRef.current, {
        center: [-8, 22],
        zoom: 3,
        dragging: false,
        touchZoom: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomControl: false,
      });

      // Mantém os créditos obrigatórios dos dados do mapa, sem a marca Leaflet.
      map.attributionControl.setPrefix(false);

      // Tile layer tech style - CartoDB Voyager (moderno e tecnológico)
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastered/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © CARTO',
        subdomains: 'abcd',
        maxZoom: 20,
      }).addTo(map);

      // Mantém África como o enquadramento principal do fundo.
      map.fitBounds([[-35, -18], [38, 53]], { padding: [20, 20] });
      // No telemóvel, um zoom menor evita cortar África no ecrã estreito.
      const zoomBoost = window.innerWidth <= 769 ? 1 : 2;
      map.setZoom(map.getZoom() + zoomBoost);

      // Contorno estilizado de Moçambique: torna o país imediatamente
      // identificável mesmo quando o mapa está atrás dos cartões de contacto.
      const mozambiqueOutline = [
        [-10.5, 40.6], [-11.8, 40.1], [-13.4, 40.2], [-15.0, 40.5],
        [-16.4, 40.7], [-18.0, 39.7], [-19.2, 38.3], [-20.7, 36.4],
        [-22.0, 35.6], [-22.2, 32.9], [-25.9, 32.5], [-26.9, 32.9],
        [-25.4, 33.8], [-24.4, 35.0], [-23.4, 35.7], [-22.5, 35.3],
        [-21.4, 35.8], [-20.0, 35.0], [-18.6, 35.1], [-17.5, 35.3],
        [-16.4, 35.2], [-15.2, 35.6], [-14.2, 35.8], [-13.3, 35.2],
        [-12.0, 35.2], [-11.0, 35.1], [-10.5, 40.6],
      ];

      L.polygon(mozambiqueOutline, {
        color: '#00e5ff',
        weight: 2,
        fillColor: '#00b4d8',
        fillOpacity: 0.42,
        className: 'mozambique-highlight',
        interactive: false,
      }).addTo(map);

      L.circleMarker([-18.665695, 35.529562], {
        radius: 22,
        color: '#ffffff',
        weight: 1,
        fillColor: '#00b4d8',
        fillOpacity: 0.18,
        className: 'mozambique-pulse',
        interactive: false,
      }).addTo(map);

      // Adicionar glow effect aos tiles com CSS
      const tiles = mapRef.current.querySelector('.leaflet-tile-container');
      if (tiles) {
        tiles.style.filter = 'hue-rotate(-10deg) saturate(1.3) brightness(1.1)';
      }

      // Criar marcador customizado para Moçambique com estilo tech
      const customIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxOCIgZmlsbD0iIzAwYjRkOCIgc3Ryb2tlPSIjZmZmZmZmIiBzdHJva2Utd2lkdGg9IjIiLz48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxMiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZmZmZmZmIiBzdHJva2Utd2lkdGg9IjEiIHN0cm9rZS1kYXNoYXJyYXk9IjIsMiIvPjwvc3ZnPg==',
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20],
      });

      // Marcador para Moçambique
      L.marker([-18.665695, 35.529562], { icon: customIcon })
        .addTo(map)
        .bindTooltip('MOÇAMBIQUE', {
          permanent: true,
          direction: 'top',
          offset: [0, -20],
          className: 'mozambique-tooltip',
        })
        .bindPopup(`
          <div style="font-family: Arial, sans-serif; text-align: center; color: #00b4d8;">
            <b style="font-size: 16px; color: #0096c7;">🇲🇿 MOÇAMBIQUE</b>
            <br>
            <span style="color: #00b4d8;">Onde a Infosfera está</span>
            <br>
            <small style="color: #666;">Tecnologia & Inovação</small>
          </div>
        `, {
          maxWidth: 250,
          className: 'tech-popup'
        });

      // Marcador para a empresa em Maputo
      const infosferaIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48cG9seWdvbiBwb2ludHM9IjIwLDAgNDAsNDAgMCw0MCIgZmlsbD0iIzAwZjBmZiIgc3Ryb2tlPSIjZmZmZmZmIiBzdHJva2Utd2lkdGg9IjIiLz48Y2lyY2xlIGN4PSIyMCIgY3k9IjI0IiByPSI0IiBmaWxsPSIjZmZmZmZmIi8+PC9zdmc+',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40],
      });

      L.marker([-25.9655, 32.5832], { icon: infosferaIcon })
        .addTo(map)
        .bindPopup(`
          <div style="font-family: Arial, sans-serif; text-align: center;">
            <b style="color: #00b4d8; font-size: 14px;">Infosfera</b>
            <br>
            <span style="color: #0096c7; font-size: 12px;">Maputo, Moçambique</span>
            <br>
            <small style="color: #00f0ff;">📍 Avenida de Maguiguana</small>
          </div>
        `, {
          maxWidth: 250,
          className: 'tech-popup'
        });

      mapRef.current.hasMap = true;
      mapRef.current.mapInstance = map;
    }

    return () => {
      // Limpeza se necessário
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      
      const formData = new FormData(formRef.current);
      formData.append('current_date', new Date().toLocaleString('pt-PT'));

     
      const result = await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      if (result.text === 'OK') {
        setSubmitStatus('success');
        formRef.current.reset();
        
        
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      console.error('Erro detalhado:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="page-header">
        <h1>Contacto</h1>
        <p>Entre em contacto conosco para mais informações</p>
      </section>
      
      <div className="map-background" ref={mapRef} aria-hidden="true">
        <div className="africa-map-label">
          <span>ÁFRICA</span>
          <strong>MOÇAMBIQUE</strong>
        </div>
      </div>
      
      <div className="container contact-content">
        <div className="contact-container">
          <div className="contact-cards">
            <div className="contact-form">
              <h2>Envie-nos uma mensagem</h2>
              
              {/* Mensagem de Sucesso */}
              {submitStatus === 'success' && (
                <div className="success-message">
                  <strong>✅ Mensagem enviada com sucesso!</strong>
                  <p>Entraremos em contacto em breve. Obrigado pelo seu contato!</p>
                </div>
              )}
              
              {/* Mensagem de Erro */}
              {submitStatus === 'error' && (
                <div className="error-message">
                  <strong>❌ Erro ao enviar mensagem.</strong>
                  <p>Por favor, tente novamente ou contacte-nos por telefone.</p>
                </div>
              )}
              
           
              <form ref={formRef} onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Nome:</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    disabled={isSubmitting}
                    placeholder="Seu nome completo"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">E-mail:</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    disabled={isSubmitting}
                    placeholder="seu@email.com"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Assunto:</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    disabled={isSubmitting}
                    placeholder="Assunto da mensagem"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Mensagem:</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    disabled={isSubmitting}
                    rows="5"
                    placeholder="Digite sua mensagem aqui..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '📧 Enviando...' : '📨 Enviar Mensagem'}
                </button>
              </form>
            </div>
            
            {/* Informações de Contato */}
            <div className="contact-info">
              <h2>Informações de Contato</h2>
              
              <div className="contact-method">
                <FaEnvelope className="contact-icon" />
                <div>
                  <h3>E-mail</h3>
                  <p>info@infosfera.co.mz</p>
                </div>
              </div>
              
              <div className="contact-method">
                <FaMapMarkerAlt className="contact-icon" />
                <div>
                  <h3>Endereço</h3>
                  <p>Maputo, Bairro da Polana Cimeto, Av.Maguiguane</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
